import { cacheManager } from '../utils/index.js';
import AuthUtils from '../utils/authUtils.js';

/**
 * User Context Service
 * Centralized user and organization state management
 * @author Zubduq Team
 */
class UserContextService {
  constructor() {
    this.currentUser = null;
    this.currentOrganization = null;
    this.organizations = [];
    this.eventListeners = new Map();
    this.isInitialized = false;
    this.isLoading = false;
    this.authStateCheckInterval = null;
    this.lastAuthState = null;
    
    // Cache configuration - use imported cacheManager as fallback
    this.cacheManager = window.cacheManager || cacheManager;
    this.cacheNamespace = 'organizations';
    // Note: Keys will be user-specific when used (e.g., 'all_organizations:userId')
    this.allOrgsKeyPrefix = 'all_organizations';
    this.userOrgKeyPrefix = 'user_organization';
    this.selectedOrgKeyPrefix = 'selected_organization';
    
    // Supported events
    this.EVENTS = {
      USER_CHANGED: 'userChanged',
      ORGANIZATION_CHANGED: 'organizationChanged',
      CONTEXT_LOADED: 'contextLoaded',
      CONTEXT_ERROR: 'contextError'
    };
    
    this.init();
  }

  /**
   * Initialize the service
   */
  async init() {
    try {
      this.isLoading = true;
      console.log('🚀 UserContextService: Initializing service');
      
      await this.loadUserContext();
      this.startAuthStateMonitoring();
      
      this.isInitialized = true;
      this.isLoading = false;
      
      console.log('✅ UserContextService: Service initialized successfully');
      this.emit(this.EVENTS.CONTEXT_LOADED, { 
        user: this.getCurrentUser(), 
        organization: this.currentOrganization 
      });
    } catch (error) {
      console.error('Error initializing UserContextService:', error);
      this.isLoading = false;
      this.emit(this.EVENTS.CONTEXT_ERROR, { error, context: 'init' });
    }
  }

  /**
   * Start monitoring authentication state changes
   */
  startAuthStateMonitoring() {
    // Check auth state every 30 seconds
    this.authStateCheckInterval = setInterval(() => {
      this.checkAuthStateChange();
    }, 30000);
    
    // Store initial auth state
    const currentUser = this.getCurrentUser();
    this.lastAuthState = currentUser ? currentUser.id : null;
  }

  /**
   * Check if authentication state has changed
   */
  async checkAuthStateChange() {
    try {
      const currentUser = this.getCurrentUser();
      const currentAuthState = currentUser ? currentUser.id : null;
      
      if (this.lastAuthState !== currentAuthState) {
        console.log('🔄 UserContextService: Authentication state changed');
        this.lastAuthState = currentAuthState;
        
        if (currentUser) {
          // User logged in or switched
          await this.loadUserContext();
        } else {
          // User logged out
          this.currentUser = null;
          this.currentOrganization = null;
          this.organizations = [];
          this.emit(this.EVENTS.USER_CHANGED, null);
          this.emit(this.EVENTS.ORGANIZATION_CHANGED, null);
        }
      }
    } catch (error) {
      console.error('Error checking auth state change:', error);
    }
  }

  /**
   * Cleanup resources
   */
  destroy() {
    try {
      // Clear the authentication state monitoring interval
      if (this.authStateCheckInterval) {
        clearInterval(this.authStateCheckInterval);
        this.authStateCheckInterval = null;
      }
      
      this.eventListeners.clear();
      this.currentUser = null;
      this.currentOrganization = null;
      this.organizations = [];
      this.isInitialized = false;
      
      console.log('🧹 UserContextService: Resources cleaned up successfully');
    } catch (error) {
      console.error('Error destroying UserContextService:', error);
    }
  }

  /**
   * Reinitialize the service
   */
  async reinitialize() {
    try {
      console.log('🔄 UserContextService: Reinitializing service');
      this.destroy();
      await this.init();
    } catch (error) {
      console.error('Error reinitializing UserContextService:', error);
      this.emit(this.EVENTS.CONTEXT_ERROR, { error, context: 'reinitialize' });
    }
  }

  /**
   * Load user context including user and organization data
   */
  async loadUserContext() {
    try {
      // Load user from AuthUtils
      const user = AuthUtils.getCurrentUser();
      if (user && (!this.currentUser || this.currentUser.id !== user.id)) {
        this.currentUser = user;
        this.emit(this.EVENTS.USER_CHANGED, this.currentUser);
      }

      // Load organization data if user is authenticated
      if (this.currentUser) {
        await this.loadOrganizationData();
      }
    } catch (error) {
      console.error('Error loading user context:', error);
      this.emit(this.EVENTS.CONTEXT_ERROR, { error, context: 'loadUserContext' });
    }
  }

  /**
   * Get current user (delegates to AuthUtils)
   * @returns {Object|null} Current user object or null
   */
  getCurrentUser() {
    return AuthUtils.getCurrentUser();
  }

  /**
   * Check if user is authenticated (delegates to AuthUtils)
   * @returns {boolean} True if user is authenticated
   */
  isAuthenticated() {
    return AuthUtils.isAuthenticated();
  }

  /**
   * Check if current user has specific role
   * @param {string} role - Role to check (admin, employee, etc.)
   * @returns {boolean} True if user has the role
   */
  hasRole(role) {
    const user = this.getCurrentUser();
    return user && user.role === role;
  }

  /**
   * Check if current user has any of the specified roles
   * @param {string[]} roles - Array of roles to check
   * @returns {boolean} True if user has any of the roles
   */
  hasAnyRole(roles) {
    const user = this.getCurrentUser();
    if (!user || !Array.isArray(roles)) return false;
    return roles.includes(user.role);
  }

  /**
   * Check if current user is admin (convenience method)
   * @returns {boolean} True if user is admin
   */
  isAdmin() {
    return AuthUtils.isAdmin();
  }

  /**
   * Check if current user is employee (convenience method)
   * @returns {boolean} True if user is employee
   */
  isEmployee() {
    return AuthUtils.isEmployee();
  }

  /**
   * Refresh all user and organization context data
   */
  async refreshContext() {
    try {
      console.log('🔄 UserContextService: Refreshing user context');
      
      // Clear organization cache
      this.cacheManager.clear(this.cacheNamespace);
      
      // Reload user context
      await this.loadUserContext();
      
      console.log('✅ UserContextService: Context refresh completed');
    } catch (error) {
      console.error('UserContextService: Failed to refresh context:', error);
      this.emit(this.EVENTS.CONTEXT_ERROR, { error, context: 'refreshContext' });
    }
  }

  /**
   * Get user-specific cache key
   * @param {string} keyPrefix - Cache key prefix
   * @returns {string|null} User-specific cache key or null if no user
   */
  getUserCacheKey(keyPrefix) {
    const currentUser = this.getCurrentUser();
    if (!currentUser || !currentUser.id) return null;
    return `${keyPrefix}:${currentUser.id}`;
  }

  /**
   * Wait for service to be ready
   * @returns {Promise} Promise that resolves when service is initialized
   */
  waitForReady() {
    return new Promise((resolve, reject) => {
      if (this.isInitialized && !this.isLoading) {
        resolve();
        return;
      }

      // Listen for context loaded event
      const onContextLoaded = () => {
        this.off(this.EVENTS.CONTEXT_LOADED, onContextLoaded);
        this.off(this.EVENTS.CONTEXT_ERROR, onContextError);
        resolve();
      };

      const onContextError = (error) => {
        this.off(this.EVENTS.CONTEXT_LOADED, onContextLoaded);
        this.off(this.EVENTS.CONTEXT_ERROR, onContextError);
        reject(error);
      };

      this.on(this.EVENTS.CONTEXT_LOADED, onContextLoaded);
      this.on(this.EVENTS.CONTEXT_ERROR, onContextError);
    });
  }

  /**
   * Get current organization
   * @returns {Object|null} Current organization object or null
   */
  getCurrentOrganization() {
    return this.currentOrganization;
  }

  /**
   * Get all organizations
   * @returns {Array} Array of organization objects
   */
  getOrganizations() {
    return this.organizations || [];
  }

  /**
   * Set current organization and persist selection
   * @param {Object} organization - Organization object to set as current
   */
  setCurrentOrganization(organization) {
    if (!organization || !organization.id) {
      console.error('UserContextService.setCurrentOrganization: Invalid organization object');
      return;
    }

    const currentUser = this.getCurrentUser();
    if (!currentUser || currentUser.role !== 'admin') {
      console.error('UserContextService.setCurrentOrganization: Only admin users can switch organizations');
      return;
    }

    // Verify this organization exists in the available organizations
    const availableOrg = this.organizations.find(org => org.id === organization.id);
    if (!availableOrg) {
      console.error(`UserContextService.setCurrentOrganization: Organization ${organization.id} not found in available organizations`);
      return;
    }

    // Set the current organization
    this.currentOrganization = availableOrg;
    
    // Save to cache for persistence
    this.saveSelectedOrganization(organization.id);
    
    // Emit organization changed event
    this.emit(this.EVENTS.ORGANIZATION_CHANGED, this.currentOrganization);
    
    console.log(`✅ UserContextService: Organization switched to ${availableOrg.name} (${availableOrg.id})`);
  }

  /**
   * Register event listener
   * Supported events: userChanged, organizationChanged, contextLoaded, contextError
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  on(event, callback) {
    try {
      if (typeof event !== 'string' || event.trim() === '') {
        console.error('UserContextService.on: event must be a non-empty string');
        return;
      }

      if (typeof callback !== 'function') {
        console.error('UserContextService.on: callback must be a function');
        return;
      }

      if (!this.eventListeners.has(event)) {
        this.eventListeners.set(event, []);
      }

      const listeners = this.eventListeners.get(event);
      
      // Prevent duplicate listeners
      if (listeners.indexOf(callback) === -1) {
        listeners.push(callback);
      }
    } catch (error) {
      console.error('Error registering event listener:', error);
    }
  }

  /**
   * Unregister event listener
   * @param {string} event - Event name
   * @param {Function} callback - Callback function to remove
   */
  off(event, callback) {
    try {
      if (typeof event !== 'string' || event.trim() === '') {
        console.error('UserContextService.off: event must be a non-empty string');
        return;
      }

      if (!this.eventListeners.has(event)) {
        return;
      }

      const listeners = this.eventListeners.get(event);
      const index = listeners.indexOf(callback);
      
      if (index > -1) {
        listeners.splice(index, 1);
      }

      if (listeners.length === 0) {
        this.eventListeners.delete(event);
      }
    } catch (error) {
      console.error('Error unregistering event listener:', error);
    }
  }

  /**
   * Emit event to all registered listeners
   * @param {string} event - Event name
   * @param {*} data - Data to pass to listeners
   */
  emit(event, data) {
    try {
      if (typeof event !== 'string' || event.trim() === '') {
        console.error('UserContextService.emit: event must be a non-empty string');
        return;
      }

      if (!this.eventListeners.has(event)) {
        return;
      }

      const listeners = this.eventListeners.get(event);
      listeners.forEach((callback, index) => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event listener ${index} for ${event}:`, error);
        }
      });
    } catch (error) {
      console.error('Error emitting event:', error);
    }
  }

  /**
   * Get service initialization status
   * @returns {object} Status object with initialization details
   */
  getStatus() {
    return {
      isInitialized: this.isInitialized,
      isLoading: this.isLoading,
      hasUser: !!this.getCurrentUser(),
      hasOrganization: !!this.currentOrganization,
      organizationCount: this.organizations.length,
      eventListenerCount: this.eventListeners.size
    };
  }

  /**
   * Wait for secureFetch to be available
   */
  async waitForSecureFetch() {
    return new Promise((resolve) => {
      if (window.secureFetch) {
        resolve();
        return;
      }
      
      // Poll for secureFetch availability
      const checkInterval = setInterval(() => {
        if (window.secureFetch) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 10);
    });
  }

  /**
   * Load organization data based on user role
   */
  async loadOrganizationData() {
    const currentUser = this.getCurrentUser();
    if (!currentUser) return;

    try {
      if (currentUser.role === 'admin') {
        await this.loadAllOrganizations();
        
        // Priority order for organization selection:
        // 1. URL parameter (immediate override)
        // 2. Cached selection (persistent preference)
        // 3. Admin's own organization (from JWT)
        // 4. First available organization (fallback)
        
        const urlParams = new URLSearchParams(window.location.search);
        const orgIdFromUrl = urlParams.get('org');
        const cachedOrgId = this.loadSelectedOrganization();
        
        if (orgIdFromUrl && this.organizations.length > 0) {
          // URL parameter takes highest priority
          const orgFromUrl = this.organizations.find(org => org.id === orgIdFromUrl);
          if (orgFromUrl) {
            this.currentOrganization = orgFromUrl;
            // Save this URL selection to cache for future visits
            this.saveSelectedOrganization(orgIdFromUrl);
            this.emit(this.EVENTS.ORGANIZATION_CHANGED, this.currentOrganization);
          } else {
            // Fallback if URL org not found
            const adminOrg = this.organizations.find(org => org.id === currentUser.organizationId);
            this.currentOrganization = adminOrg || this.organizations[0];
            this.emit(this.EVENTS.ORGANIZATION_CHANGED, this.currentOrganization);
          }
        } else if (cachedOrgId && this.organizations.length > 0) {
          // Use cached selection if no URL parameter
          const cachedOrg = this.organizations.find(org => org.id === cachedOrgId);
          if (cachedOrg) {
            this.currentOrganization = cachedOrg;
            console.log(`✅ UserContextService: Restored organization selection from cache: ${cachedOrg.name}`);
            this.emit(this.EVENTS.ORGANIZATION_CHANGED, this.currentOrganization);
          } else {
            // Cached org no longer exists, fall back to admin's org
            console.log(`⚠️ UserContextService: Cached organization ${cachedOrgId} not found, falling back`);
            const adminOrg = this.organizations.find(org => org.id === currentUser.organizationId);
            this.currentOrganization = adminOrg || this.organizations[0];
            // Clear invalid cache
            this.clearSelectedOrganization();
            this.emit(this.EVENTS.ORGANIZATION_CHANGED, this.currentOrganization);
          }
        } else if (currentUser.organizationId && this.organizations.length > 0) {
          // Prioritize admin's own organization ID from JWT token
          const adminOrg = this.organizations.find(org => org.id === currentUser.organizationId);
          this.currentOrganization = adminOrg || this.organizations[0];
          this.emit(this.EVENTS.ORGANIZATION_CHANGED, this.currentOrganization);
        } else if (this.organizations.length > 0) {
          // Final fallback to first available for admin
          this.currentOrganization = this.organizations[0];
          this.emit(this.EVENTS.ORGANIZATION_CHANGED, this.currentOrganization);
        }
      } else if (currentUser.organizationId) {
        await this.loadUserOrganization();
      }
    } catch (error) {
      console.error('Failed to load organization data:', error);
      this.emit(this.EVENTS.CONTEXT_ERROR, { error, context: 'loadOrganizationData' });
    }
  }

  /**
   * Load all organizations for admin users
   */
  async loadAllOrganizations() {
    // Get user-specific cache key
    const cacheKey = this.getUserCacheKey(this.allOrgsKeyPrefix);
    
    // Try to get from cache first (user-specific)
    if (cacheKey) {
      const cachedOrgs = this.cacheManager.get(cacheKey, this.cacheNamespace);
      if (cachedOrgs && cachedOrgs.length > 0) {
        this.organizations = cachedOrgs;
        console.log(`📂 UserContextService: Loaded ${cachedOrgs.length} organizations from cache for user`);
        return;
      }
    }

    try {
      // Wait for secureFetch to be available
      await this.waitForSecureFetch();
      
      const response = await window.secureFetch('/api/organizations');
      if (response.ok) {
        const data = await response.json();
        this.organizations = data.data || [];
        
        // Cache the data with user-specific key
        if (cacheKey) {
          this.cacheManager.set(cacheKey, this.organizations, this.cacheNamespace);
          console.log(`💾 UserContextService: Cached ${this.organizations.length} organizations for user`);
        }
      }
    } catch (error) {
      console.error('Failed to load organizations:', error);
      this.emit(this.EVENTS.CONTEXT_ERROR, { error, context: 'loadAllOrganizations' });
    }
  }

  /**
   * Load specific organization for regular users
   */
  async loadUserOrganization() {
    const currentUser = this.getCurrentUser();
    if (!currentUser || !currentUser.organizationId) return;

    // Get user-specific cache key
    const cacheKey = this.getUserCacheKey(this.userOrgKeyPrefix);
    
    // Try to get from cache first (user-specific)
    if (cacheKey) {
      const cachedOrg = this.cacheManager.get(cacheKey, this.cacheNamespace);
      if (cachedOrg) {
        this.currentOrganization = cachedOrg;
        this.emit(this.EVENTS.ORGANIZATION_CHANGED, this.currentOrganization);
        console.log(`📂 UserContextService: Loaded organization from cache for user`);
        return;
      }
    }

    try {
      // Wait for secureFetch to be available
      await this.waitForSecureFetch();
      
      const response = await window.secureFetch(`/api/organizations/${currentUser.organizationId}`);
      if (response.ok) {
        const data = await response.json();
        this.currentOrganization = data.data;
        
        // Cache the data with user-specific key
        if (cacheKey) {
          this.cacheManager.set(cacheKey, this.currentOrganization, this.cacheNamespace);
          console.log(`💾 UserContextService: Cached organization for user`);
        }
        
        this.emit(this.EVENTS.ORGANIZATION_CHANGED, this.currentOrganization);
      } else {
        // Fallback for users without organization data
        this.currentOrganization = {
          id: currentUser.organizationId,
          name: 'My Organization'
        };
        this.emit(this.EVENTS.ORGANIZATION_CHANGED, this.currentOrganization);
      }
    } catch (error) {
      console.error('Failed to load user organization:', error);
      // Fallback for users without organization data
      this.currentOrganization = {
        id: currentUser.organizationId,
        name: 'My Organization'
      };
      this.emit(this.EVENTS.ORGANIZATION_CHANGED, this.currentOrganization);
      this.emit(this.EVENTS.CONTEXT_ERROR, { error, context: 'loadUserOrganization' });
    }
  }

  /**
   * Save selected organization to cache
   * @param {string} orgId - Organization ID to save
   */
  saveSelectedOrganization(orgId) {
    const currentUser = this.getCurrentUser();
    if (!orgId || !currentUser) return;
    
    try {
      // Get user-specific cache key
      const cacheKey = this.getUserCacheKey(this.selectedOrgKeyPrefix);
      if (!cacheKey) return;
      
      // Save with long TTL (30 days) since org selection should persist across sessions
      const cacheTTL = 30 * 24 * 60 * 60 * 1000; // 30 days
      
      const cacheData = {
        orgId: orgId,
        timestamp: Date.now()
      };
      
      this.cacheManager.set(cacheKey, cacheData, this.cacheNamespace, cacheTTL);
      console.log(`💾 UserContextService: Saved selected organization ${orgId} for user ${currentUser.id}`);
    } catch (error) {
      console.error('UserContextService: Failed to save selected organization:', error);
    }
  }

  /**
   * Load selected organization from cache
   * @returns {string|null} Cached organization ID or null if not found/expired
   */
  loadSelectedOrganization() {
    const currentUser = this.getCurrentUser();
    if (!currentUser) return null;
    
    try {
      // Get user-specific cache key
      const cacheKey = this.getUserCacheKey(this.selectedOrgKeyPrefix);
      if (!cacheKey) return null;
      
      const cachedData = this.cacheManager.get(cacheKey, this.cacheNamespace);
      
      if (cachedData && cachedData.orgId) {
        console.log(`📂 UserContextService: Loaded cached organization selection ${cachedData.orgId} for user ${currentUser.id}`);
        return cachedData.orgId;
      }
      
      return null;
    } catch (error) {
      console.error('UserContextService: Failed to load selected organization from cache:', error);
      return null;
    }
  }

  /**
   * Clear selected organization from cache
   */
  clearSelectedOrganization() {
    try {
      const currentUser = this.getCurrentUser();
      if (!currentUser) return;
      
      // Get user-specific cache key
      const cacheKey = this.getUserCacheKey(this.selectedOrgKeyPrefix);
      if (!cacheKey) return;
      
      this.cacheManager.invalidate(cacheKey, this.cacheNamespace);
      console.log('🗑️ UserContextService: Cleared selected organization cache');
    } catch (error) {
      console.error('UserContextService: Failed to clear selected organization cache:', error);
    }
  }

  /**
   * Switch to different organization
   * @param {string} orgId - Organization ID to switch to
   * @param {boolean} updateUrl - Whether to update URL parameter (default: true)
   * @param {boolean} reloadPage - Whether to reload page after switch (default: true)
   */
  switchOrganization(orgId, updateUrl = true, reloadPage = true) {
    try {
      const selectedOrg = this.organizations.find(org => org.id === orgId);
      if (selectedOrg && (!this.currentOrganization || selectedOrg.id !== this.currentOrganization.id)) {
        const previousOrg = this.currentOrganization;
        this.currentOrganization = selectedOrg;
        
        // Save the selection to cache for persistence
        this.saveSelectedOrganization(orgId);
        
        console.log(`🔄 UserContextService: Switched organization from ${previousOrg?.name || 'none'} to ${selectedOrg.name}`);
        
        // Emit organization changed event
        this.emit(this.EVENTS.ORGANIZATION_CHANGED, {
          organization: this.currentOrganization,
          previousOrganization: previousOrg,
          orgId: orgId
        });
        
        // Update URL parameter if requested
        if (updateUrl) {
          const url = new URL(window.location);
          url.searchParams.set('org', orgId);
          
          if (reloadPage) {
            // Reload page to reflect organization change
            window.location.href = url.toString();
          } else {
            // Update URL without reload
            window.history.pushState({}, '', url.toString());
          }
        }
        
        return true;
      } else if (selectedOrg && this.currentOrganization && selectedOrg.id === this.currentOrganization.id) {
        console.log(`ℹ️ UserContextService: Already on organization ${selectedOrg.name}`);
        return false;
      } else {
        console.error(`UserContextService: Organization with ID ${orgId} not found`);
        this.emit(this.EVENTS.CONTEXT_ERROR, { 
          error: new Error(`Organization ${orgId} not found`), 
          context: 'switchOrganization' 
        });
        return false;
      }
    } catch (error) {
      console.error('UserContextService: Failed to switch organization:', error);
      this.emit(this.EVENTS.CONTEXT_ERROR, { error, context: 'switchOrganization' });
      return false;
    }
  }

  /**
   * Get organization by ID
   * @param {string} orgId - Organization ID
   * @returns {Object|null} Organization object or null if not found
   */
  getOrganizationById(orgId) {
    return this.organizations.find(org => org.id === orgId) || null;
  }

  /**
   * Check if current user has access to organization
   * @param {string} orgId - Organization ID to check
   * @returns {boolean} True if user has access
   */
  hasAccessToOrganization(orgId) {
    const currentUser = this.getCurrentUser();
    if (!currentUser) return false;
    
    // Admin users have access to all organizations they can see
    if (currentUser.role === 'admin') {
      return this.organizations.some(org => org.id === orgId);
    }
    
    // Regular users only have access to their own organization
    return currentUser.organizationId === orgId;
  }

  /**
   * Refresh organization data and clear cache
   */
  async refreshOrganizations() {
    try {
      // Clear organization cache
      this.cacheManager.clear(this.cacheNamespace);
      
      // Reload organization data
      await this.loadOrganizationData();
      
      console.log('🔄 UserContextService: Refreshed organization data');
    } catch (error) {
      console.error('UserContextService: Failed to refresh organizations:', error);
      this.emit(this.EVENTS.CONTEXT_ERROR, { error, context: 'refreshOrganizations' });
    }
  }
}

// ES6 module export
export default UserContextService;

// Global window compatibility
if (typeof window !== 'undefined') {
  window.UserContextService = UserContextService;
}
